/**
 * @fileoverview Chip input component for managing multiple values as tags
 * @module ctl-chip-input
 * @version 1.0.0
 * 
 * @description
 * A chip input component for entering and managing multiple values as tags/chips.
 * Supports keyboard navigation and custom chip rendering.
 * 
 * @example
 * <!-- Basic usage -->
 * <ctl-chip-input label="Tags"></ctl-chip-input>
 * 
 * @example
 * <!-- With initial values -->
 * <ctl-chip-input 
 *   label="Email Recipients" 
 *   value='["user@example.com", "admin@example.com"]'
 * ></ctl-chip-input>
 */

import { logger } from '../../../utils/logger.js';
import { safeJsonParse } from '../../../utils/safeJsonParse.js';
import { dispatchEvent } from '../../../utils/events.js';
import { shadowQuery } from '../../../utils/dom.js';

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host {
    --ctl-font: system-ui, -apple-system, Segoe UI, Roboto, Arial;
    --ctl-radius: var(--border-radius-md, 8px);

    --ctl-color-focus: var(--ctl-color-brand);
    --ctl-pad: var(--spacing-xs, 8px);
    --ctl-gap: var(--spacing-xs, 8px);

    display: block;
    font-family: var(--ctl-font);
    color: var(--ctl-color-text);
  }

  .container {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xxs, 4px);
  }

  .label {
    font-size: var(--font-size-sm, 0.875rem);
    font-weight: var(--font-weight-normal, 400);
    color: var(--ctl-color-text);
  }

  .input-wrapper {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--ctl-gap);
    padding: var(--ctl-pad);
    border-radius: var(--ctl-radius);
    border: var(--border-width-thin, 1px) solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
    box-sizing: border-box;
    transition: border-color var(--transition-fast, 150ms ease-in-out), box-shadow var(--transition-fast, 150ms ease-in-out);
    min-height: var(--input-height, 40px);
  }

  .input-wrapper:focus-within {
    border-color: var(--ctl-color-focus);
    box-shadow: var(--ctl-shadow-sm);
  }

  :host([disabled]) .input-wrapper {
    opacity: var(--opacity-60, 0.6);
    background: color-mix(in srgb, var(--color-black) 2%, transparent);
    cursor: not-allowed;
  }

  .chip {
    display: inline-flex;
    align-items: center;
    gap: var(--spacing-xxs, 4px);
    padding: var(--spacing-xxs, 4px) var(--spacing-xs, 8px);
    background: color-mix(in srgb, var(--ctl-color-focus) 10%, transparent);
    color: var(--ctl-color-focus);
    border-radius: var(--border-radius-sm, 4px);
    font-size: var(--font-size-xs, 0.75rem);
    transition: background var(--transition-fast, 150ms ease-in-out);
  }

  .chip-remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: var(--spacing-md, 16px);
    height: var(--spacing-md, 16px);
    border: none;
    background: transparent;
    color: currentColor;
    cursor: pointer;
    border-radius: 50%;
    padding: 0;
    font-size: var(--font-size-xs, 0.75rem);
    line-height: 1;
    transition: background var(--transition-fast, 150ms ease-in-out);
  }

  .chip-remove:hover {
    background: color-mix(in srgb, var(--color-black) 10%, transparent);
  }

  :host([disabled]) .chip-remove {
    cursor: not-allowed;
    opacity: var(--opacity-50, 0.5);
  }

  input {
    flex: 1;
    min-width: var(--container-xs, 120px);
    border: none;
    background: transparent;
    font-family: inherit;
    font-size: var(--font-size-sm, 0.875rem);
    color: var(--ctl-color-text);
    outline: none;
    padding: 0;
  }

  input::placeholder {
    color: var(--ctl-color-muted);
  }

  input:disabled {
    cursor: not-allowed;
  }

  .help {
    font-size: var(--font-size-xs, 0.75rem);
    color: var(--ctl-color-muted);
  }

  .error {
    font-size: var(--font-size-xs, 0.75rem);
    color: var(--ctl-color-danger);
  }

  .error:empty {
    display: none;
  }
</style>

<div class="container">
  <label class="label" part="label"></label>
  <div class="input-wrapper" part="input-wrapper">
    <input type="text" part="input" />
  </div>
  <div class="help" part="help"></div>
  <div class="error" part="error"></div>
</div>
`;

/**
 * Chip Input Web Component
 * 
 * @class CtlChipInput
 * @extends HTMLElement
 * 
 * @attr {string} label - Label text for the chip input
 * @attr {string} value - JSON array of chip values
 * @attr {string} placeholder - Placeholder text for the input
 * @attr {string} help - Help text displayed below the input
 * @attr {string} error - Error message displayed below the input
 * @attr {boolean} disabled - Whether the chip input is disabled
 * @attr {string} separator - Character(s) that trigger chip creation (default: ",")
 * @attr {string} name - Name attribute for form submission
 * 
 * @fires ctl-change - Dispatched when chips are added or removed
 * @fires ctl-change.detail.values - Array of current chip values
 * 
 * @csspart label - The label element
 * @csspart input-wrapper - The input wrapper container
 * @csspart input - The text input element
 * @csspart help - The help text element
 * @csspart error - The error message element
 * 
 * @cssprop --ctl-color-text - Text color
 * @cssprop --ctl-color-muted - Muted text color
 * @cssprop --ctl-color-border - Border color
 * @cssprop --ctl-color-surface - Background color
 * @cssprop --ctl-color-focus - Focus state and chip color
 * @cssprop --ctl-color-danger - Error state color
 * @cssprop --ctl-radius - Border radius
 */
export class CtlChipInput extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.shadowRoot.appendChild(template.content.cloneNode(true));
    
    this._input = shadowQuery(this, 'input');
    this._wrapper = shadowQuery(this, '.input-wrapper');
    this._label = shadowQuery(this, '.label');
    this._help = shadowQuery(this, '.help');
    this._error = shadowQuery(this, '.error');
    this._chips = [];
    
    // Store bound function references for proper cleanup
    this._boundHandleKeyDown = this._handleKeyDown.bind(this);
    this._boundHandleBlur = this._handleBlur.bind(this);
  }

  static get observedAttributes() {
    return ['label', 'value', 'placeholder', 'help', 'error', 'disabled', 'separator', 'name'];
  }

  connectedCallback() {
    this._input.addEventListener('keydown', this._boundHandleKeyDown);
    this._input.addEventListener('blur', this._boundHandleBlur);
    this._render();
  }

  disconnectedCallback() {
    this._input.removeEventListener('keydown', this._boundHandleKeyDown);
    this._input.removeEventListener('blur', this._boundHandleBlur);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      if (name === 'value') {
        this._chips = safeJsonParse(newValue, []);
        this._renderChips();
      }
      this._render();
    }
  }

  _handleKeyDown(e) {
    if (this.hasAttribute('disabled')) return;

    const separator = this.getAttribute('separator') || ',';
    const value = this._input.value.trim();

    // Enter or separator key
    if (e.key === 'Enter' || e.key === separator) {
      e.preventDefault();
      if (value) {
        this._addChip(value);
        this._input.value = '';
      }
    }

    // Backspace on empty input removes last chip
    if (e.key === 'Backspace' && !this._input.value && this._chips.length > 0) {
      this._removeChip(this._chips.length - 1);
    }
  }

  _handleBlur() {
    const value = this._input.value.trim();
    if (value) {
      this._addChip(value);
      this._input.value = '';
    }
  }

  _addChip(value) {
    if (!this._chips.includes(value)) {
      this._chips.push(value);
      this._renderChips();
      this._dispatchChange();
    }
  }

  _removeChip(index) {
    if (this.hasAttribute('disabled')) return;
    this._chips.splice(index, 1);
    this._renderChips();
    this._dispatchChange();
  }

  _renderChips() {
    // Remove existing chips
    const existingChips = this._wrapper.querySelectorAll('.chip');
    existingChips.forEach(chip => chip.remove());

    // Add chips before input
    this._chips.forEach((value, index) => {
      const chip = document.createElement('div');
      chip.className = 'chip';
      chip.innerHTML = `
        <span>${value}</span>
        <button class="chip-remove" data-index="${index}" ${this.hasAttribute('disabled') ? 'disabled' : ''}>×</button>
      `;

      const removeBtn = chip.querySelector('.chip-remove');
      removeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._removeChip(index);
      });

      this._wrapper.insertBefore(chip, this._input);
    });
  }

  _dispatchChange() {
    this.setAttribute('value', JSON.stringify(this._chips));
    dispatchEvent(this, 'ctl-change', { values: [...this._chips] });
  }

  _render() {
    // Label
    const label = this.getAttribute('label');
    if (label) {
      this._label.textContent = label;
      this._label.style.display = 'block';
    } else {
      this._label.style.display = 'none';
    }

    // Placeholder
    const placeholder = this.getAttribute('placeholder');
    if (placeholder) {
      this._input.placeholder = placeholder;
    }

    // Name
    const name = this.getAttribute('name');
    if (name) {
      this._input.name = name;
    }

    // Disabled
    this._input.disabled = this.hasAttribute('disabled');

    // Help text
    const help = this.getAttribute('help');
    if (help) {
      this._help.textContent = help;
      this._help.style.display = 'block';
    } else {
      this._help.style.display = 'none';
    }

    // Error message
    const error = this.getAttribute('error');
    if (error) {
      this._error.textContent = error;
      this._error.style.display = 'block';
    } else {
      this._error.style.display = 'none';
    }
  }

  /**
   * Get the current chip values
   * @returns {string[]} Array of chip values
   */
  get values() {
    return [...this._chips];
  }

  /**
   * Set chip values
   * @param {string[]} values - Array of chip values
   */
  set values(values) {
    this.setAttribute('value', JSON.stringify(values));
  }

  /**
   * Clear all chips
   */
  clear() {
    this._chips = [];
    this._input.value = '';
    this._renderChips();
    this._dispatchChange();
  }
}

if (!customElements.get("ctl-chip-input")) {
  customElements.define("ctl-chip-input", CtlChipInput);
}
